import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Loginandsignup.css';
import { useAuth } from '../context/AuthContext';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const Loginandsignup = ({ onClose, onLoginSuccess }) => {
  const [activeTab, setActiveTab] = useState('login');
  const navigate = useNavigate();
  const {
    login,
    signup,
    verifyLoginOtp,
    verifySignupOtp,
    resendOtp,
    forgotPassword,
    verifyForgotPasswordOtp,
    updatePassword
  } = useAuth();
  const [steps, setSteps] = useState({ login: 'form', signup: 'form' });
  const [isForgotFlow, setIsForgotFlow] = useState(false);
  const [forgotStep, setForgotStep] = useState('email');
  const [otpValue, setOtpValue] = useState('');
  const [pendingIdentity, setPendingIdentity] = useState({ email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showForgotConfirm, setShowForgotConfirm] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone: ''
  });
  const [forgotData, setForgotData] = useState({
    email: '',
    otp: '',
    password: '',
    confirm: ''
  });

  const getFriendlyErrorMessage = (error, context) => {
    const rawMessage = error?.message || '';
    if (!rawMessage) return 'Something went wrong.';
    const message = rawMessage.toLowerCase();

    const isInvalidOtp =
      message.includes('invalid otp') ||
      message.includes('otp') && message.includes('invalid') ||
      message.includes('validation failed');

    const isInvalidCredentials = message.includes('invalid credentials');
    const isUserNotFound =
      message.includes('user not exist') ||
      message.includes('user not found');

    const isAlreadySignedUp =
      message.includes('user already exist') ||
      message.includes('already signed up') ||
      message.includes('already signed');

    if (context === 'login_submit') {
      if (isInvalidCredentials) return 'Wrong password.';
      if (isUserNotFound) return 'Account not found.';
    }

    if (context === 'login_otp' || context === 'signup_otp' || context === 'forgot_otp') {
      if (isInvalidOtp || isInvalidCredentials) return 'Enter correct OTP.';
    }

    if (context === 'signup_submit') {
      if (isAlreadySignedUp) return 'Account already exists. Please login.';
    }

    if (context === 'forgot_email') {
      if (isUserNotFound || isInvalidCredentials) return 'Account not found.';
    }

    if (context === 'reset_password') {
      if (message.includes('password') && message.includes('confirm')) {
        return 'Passwords do not match.';
      }
    }

    return rawMessage;
  };

//login

  const currentStep = activeTab === 'login' ? steps.login : steps.signup;

  const handleInputChange = (e) => {
    if (e.target.name === 'phone') {
      const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
      setFormData({
        ...formData,
        phone: digitsOnly
      });
      return;
    }
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleForgotInputChange = (e) => {
    setForgotData({
      ...forgotData,
      [e.target.name]: e.target.value
    });
  };

  const handleAuthSuccess = (data, flow) => {
    if (onLoginSuccess) {
      onLoginSuccess(data?.user);
    }
    onClose();
    if (data?.user?.role === 'admin') {
      navigate('/admin');
      return;
    }
    if (flow === 'signup') {
      navigate('/onbording');
      return;
    }
    const onboardedStatus = data?.user?.onboarded;
    if (onboardedStatus === 'completed') {
      navigate('/dashboard');
      return;
    }
    navigate('/onbording');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');
    setIsSubmitting(true);

    try {
      if (activeTab === 'login') {
        await login({ email: formData.email, password: formData.password });
        setPendingIdentity((prev) => ({ ...prev, email: formData.email }));
        setSteps((prev) => ({ ...prev, login: 'otp' }));
        setStatusMessage('OTP sent to your email address.');
      } else {
        if (formData.phone.length !== 10) {
          setErrorMessage('Mobile number must be exactly 10 digits.');
          return;
        }
        await signup({
          email: formData.email,
          password: formData.password,
          phone: formData.phone
        });
        setPendingIdentity((prev) => ({ ...prev, phone: formData.phone }));
        setSteps((prev) => ({ ...prev, signup: 'otp' }));
        setStatusMessage('OTP sent to your mobile number.');
      }
      setOtpValue('');
    } catch (error) {
      setErrorMessage(
        getFriendlyErrorMessage(error, activeTab === 'login' ? 'login_submit' : 'signup_submit')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (!otpValue.trim()) {
      setErrorMessage('Please enter the OTP.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (activeTab === 'login') {
        const response = await verifyLoginOtp({
          email: pendingIdentity.email || formData.email,
          otp: otpValue.trim()
        });
        handleAuthSuccess(response, 'login');
      } else {
        const response = await verifySignupOtp({
          phone: pendingIdentity.phone || formData.phone,
          otp: otpValue.trim()
        });
        handleAuthSuccess(response, 'signup');
      }
    } catch (error) {
      setErrorMessage(
        getFriendlyErrorMessage(
          error,
          activeTab === 'login' ? 'login_otp' : 'signup_otp'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotEmailSubmit = async (e) => {
    e.preventDefault();
    if (!forgotData.email) {
      setErrorMessage('Please enter your email.');
      return;
    }
    setErrorMessage('');
    setStatusMessage('');
    setIsSubmitting(true);
    try {
      await forgotPassword({ email: forgotData.email });
      setForgotStep('otp');
      setStatusMessage('OTP sent to your email address.');
    } catch (error) {
      setErrorMessage(getFriendlyErrorMessage(error, 'forgot_email'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotOtpVerify = async (e) => {
    e.preventDefault();
    if (!forgotData.otp.trim()) {
      setErrorMessage('Please enter the OTP.');
      return;
    }
    setErrorMessage('');
    setStatusMessage('');
    setIsSubmitting(true);
    try {
      await verifyForgotPasswordOtp({
        email: forgotData.email,
        otp: forgotData.otp.trim()
      });
      setForgotStep('reset');
      setStatusMessage('OTP verified. Set a new password.');
    } catch (error) {
      setErrorMessage(getFriendlyErrorMessage(error, 'forgot_otp'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!forgotData.password || !forgotData.confirm) {
      setErrorMessage('Please enter and confirm your new password.');
      return;
    }
    if (forgotData.password !== forgotData.confirm) {
      setErrorMessage('Passwords do not match.');
      return;
    }
    setErrorMessage('');
    setStatusMessage('');
    setIsSubmitting(true);
    try {
      await updatePassword({
        email: forgotData.email,
        password: forgotData.password,
        cnfPassword: forgotData.confirm
      });
      setStatusMessage('Password reset successfully. Please login.');
      setIsForgotFlow(false);
      setForgotStep('email');
      setForgotData({ email: '', otp: '', password: '', confirm: '' });
      setSteps((prev) => ({ ...prev, login: 'form' }));
      setActiveTab('login');
    } catch (error) {
      setErrorMessage(getFriendlyErrorMessage(error, 'reset_password'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotResend = async () => {
    if (!forgotData.email) {
      setErrorMessage('Please enter your email first.');
      return;
    }
    setErrorMessage('');
    setStatusMessage('');
    setIsResending(true);
    try {
      await resendOtp({ type: 'forgetpassword', email: forgotData.email });
      setStatusMessage('OTP resent to your email address.');
    } catch (error) {
      setErrorMessage(error.message || 'Unable to resend OTP.');
    } finally {
      setIsResending(false);
    }
  };

  const handleResendOtp = async () => {
    const email = pendingIdentity.email || formData.email;
    if (!email) {
      setErrorMessage('Please enter your email first.');
      return;
    }
    setErrorMessage('');
    setStatusMessage('');
    setIsResending(true);
    try {
      await resendOtp({
        type: activeTab === 'login' ? 'signin' : 'signup',
        email
      });
      setStatusMessage(
        activeTab === 'login'
          ? 'OTP resent to your email address.'
          : 'OTP resent to your mobile number.'
      );
    } catch (error) {
      setErrorMessage(error.message || 'Unable to resend OTP.');
    } finally {
      setIsResending(false);
    }
  };

  const handleGoogleSignIn = () => {
    console.log('Google Sign In clicked');
    // Add your Google sign-in logic here
  };

  return (
    <div className="login-overlay" onClick={onClose}>
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>
        
        <h2 className="modal-title">
          {isForgotFlow ? 'Reset Password' : activeTab === 'login' ? 'Login Now' : 'Join Us Now'}
        </h2>
        
        <div className="tab-buttons">
          <button 
            className={`tab-btn ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('login');
              setErrorMessage('');
              setStatusMessage('');
              setOtpValue('');
              setIsForgotFlow(false);
            }}
          >
            Login
          </button>
          <button 
            className={`tab-btn ${activeTab === 'signup' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('signup');
              setErrorMessage('');
              setStatusMessage('');
              setOtpValue('');
              setIsForgotFlow(false);
            }}
          >
            Signup
          </button>
        </div>

        <div className="modal-body">
          {isForgotFlow ? (
            <form
              className="authform"
              onSubmit={
                forgotStep === 'email'
                  ? handleForgotEmailSubmit
                  : forgotStep === 'otp'
                  ? handleForgotOtpVerify
                  : handleResetPassword
              }
            >
              {forgotStep === 'email' && (
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  className="forminput"
                  value={forgotData.email}
                  onChange={handleForgotInputChange}
                  required
                  disabled={isSubmitting}
                />
              )}
              {forgotStep === 'otp' && (
                <>
                  <p className="otp-helper">
                    OTP sent to {forgotData.email}
                  </p>
                  <input
                    type="text"
                    name="otp"
                    placeholder="Enter OTP"
                    className="forminput otp-input"
                    value={forgotData.otp}
                    onChange={handleForgotInputChange}
                    maxLength={6}
                    required
                    disabled={isSubmitting}
                  />
                </>
              )}
              {forgotStep === 'reset' && (
                <>
                  <div className="password-input-group">
                    <input
                      type={showForgotPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="New Password"
                      className="forminput"
                      value={forgotData.password}
                      onChange={handleForgotInputChange}
                      required
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowForgotPassword((prev) => !prev)}
                      aria-label={showForgotPassword ? 'Hide password' : 'Show password'}
                    >
                      {showForgotPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <div className="password-input-group">
                    <input
                      type={showForgotConfirm ? 'text' : 'password'}
                      name="confirm"
                      placeholder="Confirm Password"
                      className="forminput"
                      value={forgotData.confirm}
                      onChange={handleForgotInputChange}
                      required
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowForgotConfirm((prev) => !prev)}
                      aria-label={showForgotConfirm ? 'Hide confirm password' : 'Show confirm password'}
                    >
                      {showForgotConfirm ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </>
              )}
              <div className="otp-actions">
                {forgotStep !== 'email' && (
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => {
                      setForgotStep('email');
                      setForgotData((prev) => ({ ...prev, otp: '', password: '', confirm: '' }));
                    }}
                    disabled={isSubmitting}
                  >
                    Edit email
                  </button>
                )}
                {forgotStep === 'otp' && (
                  <button
                    type="button"
                    className="link-btn"
                    onClick={handleForgotResend}
                    disabled={isSubmitting || isResending}
                  >
                    {isResending ? 'Resending...' : 'Resend OTP'}
                  </button>
                )}
                <button type="submit" className="submit-btnnn" disabled={isSubmitting}>
                  {isSubmitting
                    ? 'Please wait...'
                    : forgotStep === 'email'
                    ? 'Send OTP'
                    : forgotStep === 'otp'
                    ? 'Verify OTP'
                    : 'Update Password'}
                </button>
              </div>
            </form>
          ) : currentStep === 'form' ? (
            <form className="authform" onSubmit={handleSubmit}>
              <input
                type="email"
                name="email"
                placeholder="Email"
                className="forminput"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
              <div className="password-input-group">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  className="forminput"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {activeTab === 'login' && (
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => {
                    setIsForgotFlow(true);
                    setForgotStep('email');
                    setForgotData((prev) => ({ ...prev, email: formData.email }));
                    setErrorMessage('');
                    setStatusMessage('');
                  }}
                  disabled={isSubmitting}
                >
                  Forgot password?
                </button>
              )}
              {activeTab === 'signup' && (
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone"
                  className="forminput"
                  value={formData.phone}
                  onChange={handleInputChange}
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  required
                  disabled={isSubmitting}
                />
              )}
              <button type="submit" className="submit-btnnn" disabled={isSubmitting}>
                {isSubmitting ? 'Sending OTP...' : 'Send OTP'}
              </button>
            </form>
          ) : (
            <form className="authform" onSubmit={handleOtpVerify}>
              <p className="otp-helper">
                {activeTab === 'login'
                  ? `OTP sent to ${pendingIdentity.email || formData.email}`
                  : `OTP sent to ${pendingIdentity.phone || formData.phone}`}
              </p>
              <input
                type="text"
                name="otp"
                placeholder="Enter OTP"
                className="forminput otp-input"
                value={otpValue}
                onChange={(e) => setOtpValue(e.target.value)}
                maxLength={6}
                required
                disabled={isSubmitting}
              />
              <div className="otp-actions">
                <button
                  type="button"
                  className="link-btn"
                  onClick={() =>
                    setSteps((prev) => ({
                      ...prev,
                      [activeTab]: 'form'
                    }))
                  }
                  disabled={isSubmitting}
                >
                  Edit details
                </button>
                <button
                  type="button"
                  className="link-btn"
                  onClick={handleResendOtp}
                  disabled={isSubmitting || isResending}
                >
                  {isResending ? 'Resending...' : 'Resend OTP'}
                </button>
                <button type="submit" className="submit-btnnn" disabled={isSubmitting}>
                  {isSubmitting ? 'Verifying...' : 'Verify OTP'}
                </button>
              </div>
            </form>
          )}

          {statusMessage && <p className="auth-status">{statusMessage}</p>}
          {errorMessage && <p className="auth-error">{errorMessage}</p>}

          {!isForgotFlow && (
            <>
              <div className="divider">
                <span>OR</span>
              </div>

              {/* <button className="google-signin-btn" onClick={handleGoogleSignIn}>
                <svg className="google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Sign in with Google
              </button> */}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Loginandsignup;
